import { zeroErrorMessage } from "@/lib/zero-error";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useQuery as useZeroQuery, useZero } from "@rocicorp/zero/react";
import { appMutators, appQueries } from "@production-hub/shared";
import { toast } from "sonner";
import { generateUUID } from "@/lib/utils";
import {
  reportServerMutationFailure,
  toLegacyTimestamp,
  usePendingMutation,
  type MutationOptions,
  type ZeroMutateClient,
} from "./useZeroMutationUtils";

const COVERAGE_API_URL =
  import.meta.env.VITE_COVERAGE_API_URL || "http://localhost:3000";

// Types for database table
export interface CoverageSession {
  id: string;
  project_id: string;
  external_session_id: string;
  device_name: string | null;
  device_uid: string | null;
  started_at: string;
  ended_at: string | null;
  has_gpx: boolean;
  summary_stats: {
    avg_bitrate_kbps?: number;
    min_bitrate_kbps?: number;
    max_bitrate_kbps?: number;
    coverage_duration_seconds?: number;
    points_count?: number;
  } | null;
  created_at: string;
  updated_at: string;
}

// Types for external API
export interface ExternalSession {
  id: string;
  device_uid: string;
  device_name: string;
  started_at: number;
  ended_at: number | null;
  gpx_uploaded: number;
}

export interface Device {
  uid: string;
  name: string;
  type: string;
  status?: {
    state?: string;
    bitrate?: number;
    battery?: { percentage?: number };
  };
}

export interface LoggedDevice {
  device_uid: string;
  first_timestamp: number;
  last_timestamp: number;
  sample_count: number;
}

export interface DeviceTimeRange {
  first_timestamp: number;
  last_timestamp: number;
  sample_count: number;
}

export interface MapPoint {
  lat: number;
  lon: number;
  bitrate: number;
  color: string;
  timestamp: number;
  interpolated: boolean;
  links: Record<string, number>;
}

export interface MapData {
  points: MapPoint[];
  chartData: Array<{
    timestamp: number;
    bitrate: number;
    color: string;
    links: Record<string, number>;
  }>;
  operators: string[];
}

// === Database hooks (linked sessions) ===

type ZeroCoverageSession = {
  id: string;
  projectID: string;
  externalSessionID: string;
  deviceName?: string | null;
  deviceUID?: string | null;
  startedAt: number | string;
  endedAt?: number | string | null;
  hasGpx: boolean;
  summaryStats?: CoverageSession["summary_stats"];
  createdAt?: number | string | null;
  updatedAt?: number | string | null;
};

function toLegacyCoverageSession(row: ZeroCoverageSession): CoverageSession {
  return {
    id: row.id,
    project_id: row.projectID,
    external_session_id: row.externalSessionID,
    device_name: row.deviceName ?? null,
    device_uid: row.deviceUID ?? null,
    started_at: toLegacyTimestamp(row.startedAt),
    ended_at: row.endedAt ? toLegacyTimestamp(row.endedAt) : null,
    has_gpx: row.hasGpx,
    summary_stats: row.summaryStats ?? null,
    created_at: toLegacyTimestamp(row.createdAt),
    updated_at: toLegacyTimestamp(row.updatedAt),
  };
}

export function useProjectCoverageSessions(projectId: string | undefined) {
  const query = projectId
    ? appQueries.networkCoverageSessions.byProject({ projectID: projectId })
    : undefined;
  const [data, details] = useZeroQuery(query, { enabled: !!projectId });
  const sessions = ((data ?? []) as ZeroCoverageSession[]).map(
    toLegacyCoverageSession,
  );
  return {
    data: sessions,
    isLoading:
      !!projectId && details.type !== "complete" && sessions.length === 0,
    isError: details.type === "error",
    error:
      details.type === "error"
        ? new Error(zeroErrorMessage(details.error))
        : null,
  };
}

export function useLinkCoverageSession() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({
    projectId,
    externalSession,
  }: {
    projectId: string;
    externalSession: ExternalSession;
  }) =>
    runPending(async () => {
      const input = {
        id: generateUUID(),
        projectID: projectId,
        externalSessionID: externalSession.id,
        deviceName: externalSession.device_name,
        deviceUID: externalSession.device_uid,
        startedAt: externalSession.started_at,
        endedAt: externalSession.ended_at ?? null,
        hasGpx: externalSession.gpx_uploaded === 1,
      };
      const mutation = zero.mutate(
        appMutators.networkCoverageSessions.link(input),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Coverage session link was rejected",
      );
      return toLegacyCoverageSession(input);
    });

  return {
    mutate: (
      input: { projectId: string; externalSession: ExternalSession },
      options?: MutationOptions<CoverageSession>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Session linked to project");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to link session: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

export function useUnlinkCoverageSession() {
  const zero = useZero() as ZeroMutateClient;
  const { isPending, runPending } = usePendingMutation();
  const run = ({ id, projectId }: { id: string; projectId: string }) =>
    runPending(async () => {
      const mutation = zero.mutate(
        appMutators.networkCoverageSessions.unlink({
          id,
          projectID: projectId,
        }),
      );
      await mutation.client;
      await reportServerMutationFailure(
        mutation.server,
        "Coverage session unlink was rejected",
      );
      return { projectId };
    });

  return {
    mutate: (
      input: { id: string; projectId: string },
      options?: MutationOptions<{ projectId: string }>,
    ) => {
      void run(input)
        .then((data) => {
          toast.success("Session unlinked from project");
          options?.onSuccess?.(data);
        })
        .catch((error) =>
          toast.error("Failed to unlink session: " + error.message),
        );
    },
    mutateAsync: run,
    isPending,
  };
}

// === External API hooks (network-coverage-tester) ===

export function useCoverageDevices() {
  return useQuery({
    queryKey: ["network-coverage", "devices"],
    queryFn: async () => {
      const res = await fetch(`${COVERAGE_API_URL}/api/devices`);
      if (!res.ok) throw new Error("Failed to fetch devices");
      return res.json() as Promise<Device[]>;
    },
    staleTime: 5000, // Refresh every 5 seconds
    refetchInterval: 5000,
  });
}

export function useLoggedDevices() {
  return useQuery({
    queryKey: ["network-coverage", "logged-devices"],
    queryFn: async () => {
      const res = await fetch(`${COVERAGE_API_URL}/api/log/devices`);
      if (!res.ok) throw new Error("Failed to fetch logged devices");
      return res.json() as Promise<LoggedDevice[]>;
    },
  });
}

export function useDeviceTimeRange(deviceUid: string | undefined) {
  return useQuery({
    queryKey: ["network-coverage", "device-range", deviceUid],
    queryFn: async () => {
      if (!deviceUid) return null;
      const res = await fetch(
        `${COVERAGE_API_URL}/api/log/devices/${deviceUid}/range`,
      );
      if (!res.ok) throw new Error("Failed to fetch device time range");
      return res.json() as Promise<DeviceTimeRange>;
    },
    enabled: !!deviceUid,
  });
}

export function useExternalSessions() {
  return useQuery({
    queryKey: ["network-coverage", "external-sessions"],
    queryFn: async () => {
      const res = await fetch(`${COVERAGE_API_URL}/api/sessions`);
      if (!res.ok) throw new Error("Failed to fetch sessions");
      return res.json() as Promise<ExternalSession[]>;
    },
  });
}

export function useCoverageMapData(externalSessionId: string | undefined) {
  return useQuery({
    queryKey: ["network-coverage", "map", externalSessionId],
    queryFn: async () => {
      if (!externalSessionId) return null;
      const res = await fetch(
        `${COVERAGE_API_URL}/api/sessions/${externalSessionId}/map`,
      );
      if (!res.ok) throw new Error("Failed to fetch map data");
      return res.json() as Promise<MapData>;
    },
    enabled: !!externalSessionId,
  });
}

export function useMultipleSessionsMapData(sessionIds: string[]) {
  return useQuery({
    queryKey: ["network-coverage", "map-multiple", sessionIds],
    queryFn: async () => {
      if (sessionIds.length === 0) return [];
      const results = await Promise.all(
        sessionIds.map(async (id) => {
          const res = await fetch(`${COVERAGE_API_URL}/api/sessions/${id}/map`);
          if (!res.ok) return null;
          const data = (await res.json()) as MapData;
          return { id, ...data };
        }),
      );
      return results.filter(Boolean) as Array<MapData & { id: string }>;
    },
    enabled: sessionIds.length > 0,
  });
}

export function useCreateSessionFromLog() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      deviceUid,
      deviceName,
      startTime,
      endTime,
    }: {
      deviceUid: string;
      deviceName?: string;
      startTime: number;
      endTime: number;
    }) => {
      const res = await fetch(`${COVERAGE_API_URL}/api/sessions/from-log`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ deviceUid, deviceName, startTime, endTime }),
      });
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to create session");
      }
      return res.json() as Promise<ExternalSession>;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["network-coverage", "external-sessions"],
      });
      toast.success("Session created from log data");
    },
    onError: (error) => {
      toast.error("Failed to create session: " + error.message);
    },
  });
}

export function useUploadGpx() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      sessionId,
      gpxFile,
    }: {
      sessionId: string;
      gpxFile: File;
    }) => {
      const formData = new FormData();
      formData.append("gpx", gpxFile);

      const res = await fetch(
        `${COVERAGE_API_URL}/api/sessions/${sessionId}/gpx`,
        {
          method: "POST",
          body: formData,
        },
      );
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.error || "Failed to upload GPX");
      }
      return res.json();
    },
    onSuccess: (_, { sessionId }) => {
      queryClient.invalidateQueries({
        queryKey: ["network-coverage", "external-sessions"],
      });
      queryClient.invalidateQueries({
        queryKey: ["network-coverage", "map", sessionId],
      });
      toast.success("GPX uploaded and correlated");
    },
    onError: (error) => {
      toast.error("Failed to upload GPX: " + error.message);
    },
  });
}

export function useDeleteExternalSession() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: string) => {
      const res = await fetch(`${COVERAGE_API_URL}/api/sessions/${sessionId}`, {
        method: "DELETE",
      });
      if (!res.ok) throw new Error("Failed to delete session");
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["network-coverage", "external-sessions"],
      });
      toast.success("Session deleted");
    },
    onError: (error) => {
      toast.error("Failed to delete session: " + error.message);
    },
  });
}

export function useCoverageApiHealth() {
  return useQuery({
    queryKey: ["network-coverage", "health"],
    queryFn: async () => {
      const res = await fetch(`${COVERAGE_API_URL}/api/health`);
      if (!res.ok) throw new Error("Coverage API unavailable");
      return res.json() as Promise<{
        status: string;
        liveUConnected: boolean;
        activeRecordings: number;
      }>;
    },
    retry: false,
    staleTime: 10000,
  });
}
